import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Input,
  LoadingState,
  Radio,
  RadioGroup,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createStaffApi, TIER_LABEL, type Catalogue, type StaffHistoryEntry, type StaffMember } from "../api";
import { StaffAccessGrid } from "../StaffAccessGrid";
import { grantsFromSet, key } from "../grants";

const dateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })
    : "";

function useStaffApi() {
  const client = useApiClient();
  return useMemo(() => createStaffApi(client), [client]);
}

// ── List ────────────────────────────────────────────────────────────────────

export function StaffListPage() {
  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Staff" }]);
  const api = useStaffApi();
  const navigate = useNavigate();
  const [items, setItems] = useState<StaffMember[] | null>(null);
  const [actorTier, setActorTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.list();
      setItems(res.items);
      setActorTier(res.actor_tier);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setForbidden(true);
      else setError(apiErrorMessage(e));
    }
  }, [api]);
  useEffect(() => { void load(); }, [load]);

  if (forbidden) {
    return (
      <EmptyState
        title="Staff management is for the Super admin and Admins"
        description="Ask the Super admin if you need to add or change someone's access."
      />
    );
  }

  return (
    <Stack gap={16}>
      <SectionHeader
        title="Staff"
        description={
          actorTier === "admin"
            ? "Employees you can manage. You can give an Employee only the access you have yourself."
            : "Everyone who can sign in to HUDDL HQ, their level and what they can open. Every change is recorded."
        }
        actions={<Button onClick={() => navigate("/app/staff/new")}>Add staff</Button>}
      />
      {error ? (
        <Alert tone="danger" title="Could not load staff">
          {error} <Button variant="ghost" onClick={() => void load()}>Try again</Button>
        </Alert>
      ) : null}
      {!items && !error ? <LoadingState /> : null}
      {items && items.length === 0 ? (
        <EmptyState
          title="No staff yet"
          description={
            actorTier === "admin"
              ? "You haven't added any Employees. Add one and tick what they can work on."
              : "Only you can sign in to HQ. Add an Admin or Employee and tick the modules they can open. They get an email to set their password."
          }
          action={<Button onClick={() => navigate("/app/staff/new")}>Add staff</Button>}
        />
      ) : null}
      {items && items.length > 0 ? (
        <Card>
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Level</Th>
                <Th>Access</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((m) => (
                <Tr key={m.user_id} onClick={() => navigate(`/app/staff/${m.user_id}`)} style={{ cursor: "pointer" }}>
                  <Td>
                    <Stack gap={4}>
                      <Text role="label" as="span">{m.display_name ?? m.email ?? "Unnamed"}</Text>
                      <Text role="caption" color="secondary" as="span">{m.email}</Text>
                    </Stack>
                  </Td>
                  <Td>
                    {m.tier ? TIER_LABEL[m.tier] : ""}
                    {m.legacy_role ? <Text role="caption" color="muted" as="span"> ({m.legacy_role.replaceAll("_", " ")})</Text> : null}
                  </Td>
                  <Td>{m.tier === "super_admin" ? "Everything" : `${new Set(m.grants.map((g) => g.module)).size} modules`}</Td>
                  <Td>
                    <StatusChip tone={m.active ? "success" : "neutral"}>{m.active ? "Active" : "Deactivated"}</StatusChip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Card>
      ) : null}
    </Stack>
  );
}

// ── Create ──────────────────────────────────────────────────────────────────

export function StaffCreatePage() {
  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Staff", to: "/app/staff" }, { label: "Add staff" }]);
  const api = useStaffApi();
  const navigate = useNavigate();
  const toast = useToast();
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [tier, setTier] = useState<"admin" | "employee">("employee");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.catalogue().then(setCatalogue).catch((e) => setLoadError(apiErrorMessage(e)));
  }, [api]);

  // Switching to Employee drops anything only an Admin can hold.
  const changeTier = (next: "admin" | "employee") => {
    setTier(next);
    if (next === "employee" && catalogue) {
      const adminOnly = new Set(catalogue.modules.filter((m) => m.min_tier === "admin").map((m) => m.module));
      setSelected(new Set([...selected].filter((k) => !adminOnly.has(k.split(":")[0] ?? ""))));
    }
  };

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = validEmail && name.trim().length > 0 && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.create({
        email: email.trim(),
        display_name: name.trim(),
        tier,
        grants: grantsFromSet(selected),
        reason: reason.trim() || null,
      });
      toast.push({
        tone: res.invite_email === "failed" ? "warning" : "success",
        title: "Staff account ready",
        description: res.created_login
          ? res.invite_email === "sent"
            ? `We emailed ${email.trim()} a link to set their password.`
            : `The account exists, but the password email did not send. Open their profile and use Resend password email.`
          : `${email.trim()} already had a HUDDL account. It now has HQ access; they sign in with their existing password.`,
      });
      navigate(`/app/staff/${res.user_id}`);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return <Alert tone="danger" title="Could not open this form">{loadError}</Alert>;
  }
  if (!catalogue) return <LoadingState />;

  return (
    <Stack gap={16}>
      <SectionHeader
        title="Add staff"
        description="They get an email to choose a password and can then sign in to HQ. If the email already has a HUDDL account, that account is given HQ access instead."
      />
      <Card>
        <Stack gap={16}>
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required />
          <RadioGroup name="tier" value={tier} onChange={(v) => changeTier(v as "admin" | "employee")} legend="Level">
            {catalogue.tiers.includes("admin") ? (
              <Radio value="admin" label="Admin: manages Employees and works across the modules ticked below" />
            ) : null}
            <Radio value="employee" label="Employee: works only on what is assigned to them, in the modules ticked below" />
          </RadioGroup>
        </Stack>
      </Card>
      <Card>
        <Stack gap={12}>
          <Text role="title" as="h3">What they can open</Text>
          <Text role="body" color="secondary" as="p">
            Leave everything unticked to start with no access. Player ID and selfie review stays with the Super admin unless you tick it.
          </Text>
          <StaffAccessGrid modules={catalogue.modules} tier={tier} selected={selected} onChange={setSelected} disabled={busy} />
        </Stack>
      </Card>
      <Card>
        <Textarea label="Note for the record (optional)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
      </Card>
      {error ? <Alert tone="danger" title="Not created">{error}</Alert> : null}
      <Flex gap={8}>
        <Button onClick={() => void submit()} disabled={!canSubmit} loading={busy}>Create staff account</Button>
        <Button variant="ghost" onClick={() => navigate("/app/staff")} disabled={busy}>Cancel</Button>
      </Flex>
    </Stack>
  );
}

// ── Detail ──────────────────────────────────────────────────────────────────

const HISTORY_LABEL: Record<string, string> = {
  "staff.access_created": "Given HQ access",
  "staff.access_changed": "Access changed",
  "staff.deactivated": "Deactivated",
  "staff.reactivated": "Reactivated",
};

export function StaffDetailPage() {
  const { userId = "" } = useParams<{ userId: string }>();
  const api = useStaffApi();
  const toast = useToast();
  const navigate = useNavigate();
  const [member, setMember] = useState<StaffMember | null>(null);
  const [history, setHistory] = useState<StaffHistoryEntry[]>([]);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tier, setTier] = useState<"admin" | "employee">("employee");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState("");

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Staff", to: "/app/staff" },
    { label: member?.display_name ?? member?.email ?? "Staff member" },
  ]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [res, cat] = await Promise.all([api.get(userId), api.catalogue()]);
      setMember(res.member);
      setHistory(res.history);
      setCatalogue(cat);
      setTier(res.member.tier === "admin" ? "admin" : "employee");
      setSelected(new Set(res.member.grants.map(key)));
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 403)) setNotFound(true);
      else setError(apiErrorMessage(e));
    }
  }, [api, userId]);
  useEffect(() => { void load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, success: string) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      toast.push({ tone: "success", title: success });
      setEditing(false);
      setDeactivateOpen(false);
      setDeactivateReason("");
      setReason("");
      await load();
    } catch (e) {
      setActionError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <EmptyState
        title="Not found"
        description="This person is not on the team, or they are outside what you can manage."
        action={<Button variant="secondary" onClick={() => navigate("/app/staff")}>Back to Staff</Button>}
      />
    );
  }
  if (error) return <Alert tone="danger" title="Could not load this staff member">{error}</Alert>;
  if (!member || !catalogue) return <LoadingState />;

  const isSuper = member.tier === "super_admin";
  const granted = new Set(member.grants.map(key));

  return (
    <Stack gap={16}>
      <SectionHeader
        title={member.display_name ?? member.email ?? "Staff member"}
        description={`${member.email ?? ""}${member.since ? ` · since ${dateTime(member.since)}` : ""}`}
        actions={
          isSuper ? null : (
            <Flex gap={8}>
              {member.active && !editing ? <Button variant="secondary" onClick={() => setEditing(true)}>Change access</Button> : null}
              {member.active ? (
                <Button variant="ghost" onClick={() => void run(() => api.resendInvite(userId), "Password email sent")} disabled={busy}>
                  Resend password email
                </Button>
              ) : null}
              {member.active ? (
                <Button variant="danger" onClick={() => setDeactivateOpen(true)} disabled={busy}>Deactivate</Button>
              ) : (
                <Button onClick={() => void run(() => api.reactivate(userId), "Reactivated")} loading={busy}>Reactivate</Button>
              )}
            </Flex>
          )
        }
      />
      <Flex gap={8}>
        <Badge tone="info">{member.tier ? TIER_LABEL[member.tier] : ""}</Badge>
        <StatusChip tone={member.active ? "success" : "neutral"}>{member.active ? "Active" : "Deactivated: cannot open HQ"}</StatusChip>
      </Flex>
      {actionError ? <Alert tone="danger" title="That change was not made">{actionError}</Alert> : null}

      {isSuper ? (
        <Alert tone="info" title="Super admin">This account can do everything in HQ. It cannot be changed or deactivated here.</Alert>
      ) : editing ? (
        <Card>
          <Stack gap={12}>
            {catalogue.tiers.includes("admin") ? (
              <RadioGroup name="tier" value={tier} onChange={(v) => setTier(v as "admin" | "employee")} legend="Level">
                <Radio value="admin" label="Admin" />
                <Radio value="employee" label="Employee" />
              </RadioGroup>
            ) : null}
            <StaffAccessGrid modules={catalogue.modules} tier={tier} selected={selected} onChange={setSelected} disabled={busy} />
            <Textarea label="Why (kept in the record)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
            <Flex gap={8}>
              <Button
                loading={busy}
                onClick={() => void run(() => api.setAccess(userId, { tier, grants: grantsFromSet(selected), reason: reason.trim() || null }), "Access saved")}
              >
                Save access
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => { setEditing(false); setSelected(new Set(member.grants.map(key))); }}>
                Cancel
              </Button>
            </Flex>
          </Stack>
        </Card>
      ) : (
        <Card>
          <Stack gap={8}>
            <Text role="title" as="h3">What they can open</Text>
            {member.grants.length === 0 ? (
              <Text role="body" color="secondary" as="p">Nothing yet. Use Change access to tick modules.</Text>
            ) : (
              catalogue.modules
                .filter((m) => granted.has(`${m.module}:view`))
                .map((m) => (
                  <Flex key={m.module} gap={8} align="center">
                    <Text role="label" as="span">{m.label}</Text>
                    <Text role="caption" color="secondary" as="span">
                      {granted.has(`${m.module}:manage`) ? "View and manage" : "View"}
                      {granted.has(`${m.module}:view_all`) ? ", sees everyone's work" : m.work_scope && member.tier === "employee" ? ", only their assigned work" : ""}
                    </Text>
                    {m.sensitive ? <Badge>Sensitive</Badge> : null}
                  </Flex>
                ))
            )}
          </Stack>
        </Card>
      )}

      <Card>
        <Stack gap={8}>
          <Text role="title" as="h3">History</Text>
          {history.length === 0 ? (
            <Text role="body" color="secondary" as="p">No changes recorded yet.</Text>
          ) : (
            history.map((h) => (
              <Stack key={h.id} gap={4}>
                <Text role="label" as="span">
                  {HISTORY_LABEL[h.action] ?? h.action} · {dateTime(h.at)}{h.by ? ` · by ${h.by}` : ""}
                </Text>
                {h.after?.added?.length ? <Text role="caption" color="secondary" as="span">Added: {h.after.added.join(", ")}</Text> : null}
                {h.after?.removed?.length ? <Text role="caption" color="secondary" as="span">Removed: {h.after.removed.join(", ")}</Text> : null}
                {h.reason ? <Text role="caption" color="muted" as="span">{h.reason}</Text> : null}
              </Stack>
            ))
          )}
        </Stack>
      </Card>

      <Dialog
        open={deactivateOpen}
        onClose={() => (busy ? undefined : setDeactivateOpen(false))}
        title="Deactivate this staff account?"
        actions={
          <Flex gap={8}>
            <Button variant="ghost" onClick={() => setDeactivateOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              variant="danger"
              loading={busy}
              disabled={deactivateReason.trim().length < 3}
              onClick={() => void run(() => api.deactivate(userId, deactivateReason.trim()), "Deactivated")}
            >
              Deactivate
            </Button>
          </Flex>
        }
      >
        <Stack gap={8}>
          <Text role="body" as="p">
            They lose HQ access on their next click. Their HUDDL Play account is not affected, and you can reactivate them later.
          </Text>
          <Textarea label="Reason (kept in the record)" value={deactivateReason} onChange={(e) => setDeactivateReason(e.target.value)} maxLength={500} />
        </Stack>
      </Dialog>
    </Stack>
  );
}
